/**
 * Where a completed intake goes next.
 *
 * 🔑 SINCE CARD 40751 (2026-09-15) THE DEFAULT IS `/checkout`, NOT `/calendar`.
 * DrCare consultations are ASYNCHRONOUS: the member submits an intake, a physician
 * reviews it, and an approval or denial arrives as a status update. There is no
 * routine call to book, so a completed intake goes straight to payment.
 *
 * Exactly ONE branch still diverts to a booking step, and it is unchanged:
 * a DoctorNow product whose `doctor_now_config.requires_appointment` is `true`
 * (card 28373) books a REAL vendor slot at `/doctor-now-schedule` first and
 * continues to `/checkout` from there.
 *
 * ⚠️ TRT NO LONGER NEEDS ITS OWN BRANCH, AND THAT IS WHY THE `isTrtProduct` CHECK
 * IS GONE. Testosterone Cream and Cypionate are pinned to `provider_type='DrCare'`
 * by design (card 2xxx), so they used to fall through to `/calendar` and needed an
 * explicit escape hatch to reach checkout. Now that every DrCare product reaches
 * checkout, that escape hatch is the default and keeping it would be dead logic.
 * ⛔ Do not read the removal as "TRT changed" — TRT's DESTINATION is byte-identical.
 *
 * A CONTROLLED product (card 7237) is still decided BEFORE any of that: it must
 * stop at `/lab-upload` first, and only then continue to wherever it would have
 * gone anyway. That ordering is the whole point — the lab gate is clinical, so it
 * outranks the routing convenience above it.
 *
 * ⚠️ `CALENDAR_ROUTE` REMAINS EXPORTED ON PURPOSE even though nothing here returns
 * it: scheduling is conditional, not deleted. `web/utils/asyncConsultCopy.js`
 * `shouldOfferScheduling()` is the predicate that decides when a surface may still
 * offer a booking, and card 40756 owns the `/calendar` page itself.
 *
 * Extracted from `web/pages/user/IntakePage.jsx` rather than left inline: that
 * page has TWO navigation points that must agree, and they have drifted apart
 * before (see LEARNINGS, "DoctorNow vs DrCare Flow Divergence at IntakePage
 * Navigation"). One exported pure function is the only way both sites can be
 * covered by one test.
 */

const CHECKOUT_ROUTE = '/checkout';
const CALENDAR_ROUTE = '/calendar';
const LAB_UPLOAD_ROUTE = '/lab-upload';
const DOCTOR_NOW_SCHEDULE_ROUTE = '/doctor-now-schedule';

/**
 * The `documents[].type` a lab report is filed under. This is the SAME
 * discriminator `api/controllers/intakeController.js` writes for the card-1254
 * intake lab question, deliberately: a patient who already answered that
 * question has genuinely supplied labs and must not be asked twice.
 */
const LAB_DOCUMENT_TYPE = 'trt_lab_results';

/** True when the product is served by the DoctorNow async provider network. */
const isDoctorNowProduct = (product) =>
  (product?.providerType ?? product?.provider_type) === 'Doctor Now';

/**
 * True when the product carries the controlled-substance designation (card 7235).
 *
 * Dual-case by the project's `??` convention — the customer API returns
 * `isControlledSubstance` (camelCase, via `toCamelCaseKeys`) while a row read
 * straight from Postgres carries `is_controlled_substance`.
 *
 * ⛔ Deliberately NOT derived from `isTrtProduct`: `TRT_PRODUCT_SLUGS` pins only
 * `testosterone-cream` and `testosterone-cypionate`, while `Depo-Testosterone`
 * — testosterone cypionate, a Schedule III controlled substance — is excluded
 * from that list by design. Designating from the TRT list would leave a real
 * controlled substance ungated. Read the flag, never the TRT list.
 *
 * Total function: anything that is not boolean `true` is not controlled, so an
 * absent, null or string field fails CLOSED to "not controlled" on the client
 * while the server remains the real guard.
 */
const isControlledSubstanceProduct = (product) =>
  (product?.isControlledSubstance ?? product?.is_controlled_substance) === true;

/**
 * True when this intake submission already carries a lab document.
 *
 * Total function: a non-array `documents` (absent, null, an object) returns
 * false, so a malformed submission is treated as "labs missing" and the gate
 * holds rather than opening.
 */
const hasLabDocument = (submission) =>
  Array.isArray(submission?.documents) &&
  submission.documents.some((doc) => doc?.type === LAB_DOCUMENT_TYPE);

/**
 * Card 49666: no DoctorNow product books an appointment before checkout any more.
 *
 * Every consultation is asynchronous, so this answers `false` unconditionally and
 * `resolvePostLabRoute` below therefore sends every completed intake to `/checkout`.
 * `DoctorNowSchedulePage` also asks this and redirects away when it is false, so the
 * `/doctor-now-schedule` route is left registered but unreachable in practice rather
 * than being deleted (board card 28373 built it; #49666 retires it reversibly).
 *
 * ROLLBACK: restore the dual-case `=== true` read of `doctor_now_config`.
 */
const requiresDoctorNowAppointment = () => false;

/**
 * The vendor encounter type the picker asks availability for, or null.
 *
 * Lives beside the flag above rather than in the page because it reads the SAME
 * dual-case config object, and splitting the two is how the camelCase half of one of
 * them silently goes missing.
 */
const readDoctorNowEncounterTypeId = (product) => {
  const config = product?.doctorNowConfig ?? product?.doctor_now_config ?? null;
  return config?.encounterTypeId ?? config?.encounter_type_id ?? null;
};

/**
 * Where a completed intake goes once the lab requirement is out of the way.
 * This is what `/lab-upload` hands off to — calling `resolveNextRoute` from there
 * would send a controlled product straight back to the lab step forever.
 *
 * Total function: null / undefined / garbage answers `/checkout`, the async default.
 */
const resolvePostLabRoute = (product) =>
  isDoctorNowProduct(product) && requiresDoctorNowAppointment(product)
    ? DOCTOR_NOW_SCHEDULE_ROUTE
    : CHECKOUT_ROUTE;

/**
 * Route a completed intake. Total function — null / undefined / garbage
 * returns `/checkout`, the asynchronous default (card 40751).
 */
const resolveNextRoute = (product) =>
  isControlledSubstanceProduct(product) ? LAB_UPLOAD_ROUTE : resolvePostLabRoute(product);

/**
 * May this customer be shown checkout for this product?
 *
 * Pure, so the route guard in `web/App.jsx` stays a thin effect wrapper and this
 * decision is unit-testable on its own. Fails CLOSED: while the submission is
 * still loading the caller passes `undefined` and a controlled product is
 * blocked, so the gate can never flicker open during a fetch.
 */
const isCheckoutBlockedPendingLabs = (product, submission) =>
  isControlledSubstanceProduct(product) && !hasLabDocument(submission);

/**
 * Does this list of the user's intake submissions contain one for THIS product?
 *
 * `GET /intake-forms/my-submissions` is UNFILTERED — `intakeSubmissionRepository.findByUser`
 * is `.eq('user_id', ...)` with no product filter — so `submissions.length > 0` answers
 * "has this user ever submitted ANY intake?". Both `OfferPage` call sites ask a different
 * question: "has this user completed intake FOR THIS PRODUCT?". Matching on the product id
 * is what makes the fallback answer the question its own comment states.
 *
 * A row carries the id twice: `productId` (from `toCamelCaseKeys`) and the nested
 * `product.id` (from the joined `product:products(id, name, slug)` select), so read both.
 *
 * Total function: non-array input, and a null/undefined `productId`, both return false —
 * a missing id must never satisfy an intake gate.
 */
const hasSubmissionForProduct = (submissions, productId) =>
  Array.isArray(submissions) &&
  productId != null &&
  submissions.some((submission) => (submission?.productId ?? submission?.product?.id) === productId);

export {
  CHECKOUT_ROUTE,
  CALENDAR_ROUTE,
  LAB_UPLOAD_ROUTE,
  DOCTOR_NOW_SCHEDULE_ROUTE,
  LAB_DOCUMENT_TYPE,
  isDoctorNowProduct,
  requiresDoctorNowAppointment,
  readDoctorNowEncounterTypeId,
  isControlledSubstanceProduct,
  hasLabDocument,
  resolvePostLabRoute,
  resolveNextRoute,
  isCheckoutBlockedPendingLabs,
  hasSubmissionForProduct
};
